import { useEffect, useState } from "react";
import { Plus, X } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { inputCls } from "@/lib/catalog";
import { CATEGORY_LABELS } from "@/lib/components";

const UNITS = [["piece", "each"], ["sash", "per opening sash"], ["panel", "per panel"], ["sqft", "per sq ft"], ["lin_ft", "per linear ft (width)"]];
const CATS = Object.entries(CATEGORY_LABELS).filter(([k]) => k !== "screen");
const small = inputCls.replace("w-full", "");

function Row({ e, i, set, remove }) {
  const flip = (c) => set({ applies_to: e.applies_to.includes(c) ? e.applies_to.filter((x) => x !== c) : [...e.applies_to, c] });
  return (
    <div data-testid={`extra-lib-row-${i}`} className="rounded-xl border border-slate-800 p-3">
      <div className="flex flex-wrap items-center gap-2">
        <input aria-label="Name" data-testid={`extra-lib-name-${i}`} placeholder="e.g. Nail fin" value={e.name} onChange={(x) => set({ name: x.target.value })} className={`${small} w-56`} />
        <input aria-label="Price" data-testid={`extra-lib-rate-${i}`} inputMode="decimal" value={e.rate} onChange={(x) => set({ rate: x.target.value })} className={`${small} w-24 text-right font-mono-num`} />
        <select aria-label="Unit" data-testid={`extra-lib-unit-${i}`} value={e.unit} onChange={(x) => set({ unit: x.target.value })} className={`${small} w-48`}>
          {UNITS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <label className="flex items-center gap-1.5 text-xs text-slate-300"><input type="checkbox" data-testid={`extra-lib-default-${i}`} checked={e.default_on} onChange={(x) => set({ default_on: x.target.checked })} /> On by default</label>
        <button aria-label="Remove" data-testid={`extra-lib-remove-${i}`} onClick={remove} className="ml-auto p-1 text-slate-500 hover:text-red-300"><X className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 flex flex-wrap gap-3 text-xs text-slate-400">
        <span>Applies to{e.applies_to.length ? "" : " (all windows & doors)"}:</span>
        {CATS.map(([k, l]) => (
          <label key={k} className="flex items-center gap-1"><input type="checkbox" data-testid={`extra-lib-applies-${i}-${k}`} checked={e.applies_to.includes(k)} onChange={() => flip(k)} /> {l}</label>
        ))}
      </div>
    </div>
  );
}

// Extras library: each extra is added to every matching product line (as an option; "on by default" ticks it when the line is picked).
export function ExtrasLibrarySection() {
  const [list, setList] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => { api.get("/settings/extras-library").then((r) => setList(r.data)).catch((e) => toast.error(errorMessage(e))); }, []);
  if (!list) return null;
  const save = async () => {
    const bad = list.find((e) => !e.name.trim() || Number.isNaN(Number(e.rate)) || Number(e.rate) < 0);
    if (bad) return setErr(`${bad.name || "An extra"}: needs a name and a price of 0 or more`);
    setErr("");
    try {
      const { data } = await api.put("/settings/extras-library", { extras: list.map((e) => ({ ...e, rate: Number(e.rate) })) });
      setList(data.extras);
      toast.success(`Extras saved · ${data.lines_updated} product lines updated`);
    } catch (e) { setErr(errorMessage(e)); }
  };
  return (
    <section data-testid="extras-library-section" className="rounded-2xl border border-slate-800 p-5">
      <h2 className="text-base font-bold text-slate-100 md:text-lg">Extras library</h2>
      <p className="mt-1 text-sm text-slate-400">Extras offered on quote lines. Each one is added to every product line it applies to. Extras typed on a single product line stay as they are.</p>
      <div className="mt-3 space-y-2">
        {list.map((e, i) => <Row key={i} e={e} i={i} set={(p) => setList(list.map((x, j) => (j === i ? { ...x, ...p } : x)))} remove={() => setList(list.filter((_, j) => j !== i))} />)}
      </div>
      <button data-testid="extra-lib-add" onClick={() => setList([...list, { name: "", rate: 0, unit: "piece", applies_to: [], default_on: false }])} className="mt-2 inline-flex items-center gap-1 text-xs text-amber-300"><Plus className="h-3 w-3" /> Add extra</button>
      {err && <p data-testid="extras-library-error" className="mt-2 text-xs text-red-400">{err}</p>}
      <div><button data-testid="extras-library-save" onClick={save} className="mt-3 rounded-full bg-amber-500 px-4 py-1.5 text-sm font-semibold text-slate-950 hover:bg-amber-400">Save extras</button></div>
    </section>
  );
}
